/**
 * Sujet du formulaire de contact : pre-selection par l'URL, et menage des
 * champs qu'il cache.
 *
 * Progressif. Le devoilement des champs sur-mesure est fait en CSS
 * (`component-go-contact.css`), donc sans JS les boutons cochent et le bloc
 * apparait quand meme. Ce composant n'ajoute que ce que le CSS ne peut pas :
 *
 * - Appliquer `?sujet=` au chargement. Liquid ne lit aucun parametre d'URL —
 *   c'est la meme contrainte que le filtre de la page Collection — donc la
 *   pre-selection depuis « Demandez un echantillon » ou « Sur-mesure » ne peut
 *   se faire qu'ici. Sans JS, le formulaire s'ouvre sur « Autre » : le visiteur
 *   a un formulaire complet devant lui, il lui reste un bouton a cocher.
 *
 * - Desactiver les champs sur-mesure quand ils sont caches. Un champ cache par
 *   le CSS est toujours soumis : sans ca, un projet decrit puis abandonne au
 *   profit d'un autre sujet partirait dans l'e-mail, sous un sujet qui le
 *   dement. `disabled` est ce qui retire un champ de l'envoi, pas `hidden`.
 *
 * La correspondance entre valeur d'URL et sujet vit dans le balisage
 * (`data-sujet` sur chaque radio), pas ici : ajouter un sujet au formulaire ne
 * demande pas de revenir dans ce fichier.
 */
class GoContactSubject extends HTMLElement {
  connectedCallback() {
    this.radios = Array.from(this.querySelectorAll('.go-contact__radio'));
    this.bespoke = this.querySelector('.go-contact__bespoke');
    if (this.radios.length === 0) return;

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    this.applyUrlSubject();
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  onChange(event) {
    if (event.target.classList.contains('go-contact__radio')) this.sync();
  }

  /**
   * Coche le sujet demande par `?sujet=`. Une valeur inconnue, ou absente,
   * laisse le sujet coche cote serveur — « Autre », l'entree du menu.
   */
  applyUrlSubject() {
    let asked;
    try {
      asked = new URLSearchParams(window.location.search).get('sujet');
    } catch (error) {
      return;
    }
    if (!asked) return;

    const wanted = asked.trim().toLowerCase();
    const radio = this.radios.find((input) => input.dataset.sujet === wanted);
    if (radio) radio.checked = true;
  }

  /**
   * Aligne l'etat des champs sur-mesure sur le sujet coche.
   *
   * La classe double le selecteur `:has()` de la feuille de style plutot que de
   * le remplacer : le CSS reste seul maitre de l'affichage la ou il suffit, et
   * la classe rattrape un navigateur sans `:has()`, ou le bloc resterait cache
   * pour toujours.
   */
  sync() {
    if (!this.bespoke) return;

    const bespoke = this.radios.find((input) => input.dataset.sujet === 'sur-mesure');
    const active = Boolean(bespoke && bespoke.checked);

    this.classList.toggle('go-contact--bespoke', active);
    this.bespoke.querySelectorAll('input, textarea, select').forEach((field) => {
      field.disabled = !active;
    });
  }
}

// Meme garde que les autres composants GO! : la section est repetable, et deux
// balises <script src> identiques sur une page s'executent deux fois.
if (!customElements.get('go-contact-subject')) {
  customElements.define('go-contact-subject', GoContactSubject);
}
